import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Element } from 'react-scroll';
import {
  SalvButton,
  SalvProgressCircle,
  SalvTable,
  SalvTablePaginationType,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { InternalApiCaseTransaction, InternalApiSortDirection } from '../../../api-internal';
import { useDetachCaseTransactions, useQueryCaseTransactionsPage } from './caseTransactionQueryActions';
import { formatDate } from '../../../common/datetime/FormattedDateTime';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { showFeedback } from '../../../feedback/feedbackReducer';
import Has from '../../../auth/Has';
import { CAN_MODIFY_CASE } from '../../../auth/permissions';
import DataLoadingErrorFallback from '../../../common/errors/DataLoadingErrorFallback';
import { createColumnHelper, PaginationState, SortingState, Updater } from '@tanstack/react-table';
import { getTransactionPath, InternalNavigation } from '../../../navigation/helper';
import { useNavigate, createSearchParams } from 'react-router';
import { SearchParamKeys } from '../../../common/searchParamKeys';
import '../../../search/TransactionSearchResults.scss';
import useTransactionColumns, { BaseTransaction } from '../../../transaction/table/useTransactionColumns';
import { getOrderByAndDirection } from '../../../common/sort';

interface CaseTransactionsProps {
  handle: string;
}

export const CASE_TRANSACTIONS_SCROLL_NAME = 'case-transactions';

const PAGE_SIZE = 10;

export const LIMIT = 100;

interface CaseTransaction extends BaseTransaction {
  createdBy: InternalApiCaseTransaction['createdBy'];
  createdAt: InternalApiCaseTransaction['createdAt'];
}

interface SortState {
  orderBy?: string;
  direction?: InternalApiSortDirection;
}

const CaseTransactions = ({ handle }: CaseTransactionsProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState<string>();
  const [sort, setSort] = useState<SortState>({});

  const {
    data: transactionsPage,
    isPending,
    isFetching,
    isError,
    error,
  } = useQueryCaseTransactionsPage({
    handle,
    page,
    size: PAGE_SIZE,
    query: query || undefined,
    orderBy: sort.orderBy,
    direction: sort.direction,
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [transactionsPage]);

  const detachCaseTransactions = useDetachCaseTransactions();

  const data: CaseTransaction[] = useMemo(() => {
    return (
      transactionsPage?.content.map((txn) => ({
        id: txn.transaction.id,
        attributes: txn.transaction.attributes,
        createdBy: txn.createdBy,
        createdAt: txn.createdAt,
      })) ?? []
    );
  }, [transactionsPage]);

  const {
    columns: transactionColumns,
    isPending: isColumnsPending,
    isError: isColumnsError,
  } = useTransactionColumns<CaseTransaction>({
    transactions: data,
    enableSorting: true,
  });

  const [selectedRows, setSelectedRows] = useState<CaseTransaction[]>([]);

  const setPagination = useCallback((newPagination: PaginationState) => {
    setPage(newPagination.pageIndex);
  }, []);

  const sortingState: SortingState = useMemo(
    () => [
      {
        id: sort.orderBy || '',
        desc: sort.direction === InternalApiSortDirection.DESC,
      },
    ],
    [sort]
  );

  const setSorting = useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      const { orderBy, direction } = getOrderByAndDirection(updaterOrValue, sortingState);
      setSort({ orderBy, direction });
      setPage(0);
    },
    [sortingState]
  );

  const handleDetach = async () => {
    const count = selectedRows.length;
    const isConfirmed = await dispatch(
      showConfirmationModal({
        title: 'Detach transactions?',
        description: `${count} transaction${count > 1 ? 's' : ''} will be detached from this case.`,
        confirmationText: 'Yes, detach',
      })
    );

    if (!isConfirmed) return;

    try {
      await detachCaseTransactions.mutateAsync({
        handle,
        externalIds: selectedRows.map((row) => row.id),
      });
      dispatch(showFeedback({ message: 'Transactions detached successfully', success: true }));
      setSelectedRows([]);
    } catch {
      dispatch(showFeedback({ message: 'Failed to detach transactions', success: false }));
    }
  };

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<CaseTransaction>();
    return [
      ...transactionColumns,
      columnHelper.accessor('createdAt', {
        header: 'Attached',
        cell: ({ getValue, row }) => (
          <SalvTooltipWrapper
            tooltip={`${row.original.createdBy.name}, ${formatDate(getValue(), true)}`}
            placement='top'
          >
            {formatDate(getValue())}
          </SalvTooltipWrapper>
        ),
        enableSorting: false,
      }),
    ];
  }, [transactionColumns]);

  if (isError || isColumnsError) {
    return <DataLoadingErrorFallback message='Could not load Case Transactions' error={[error]} />;
  }

  const isLoading = isPending || isColumnsPending;

  return (
    <Element name={CASE_TRANSACTIONS_SCROLL_NAME}>
      {isLoading ? (
        <SalvProgressCircle ariaLabel={'Loading Transactions'} />
      ) : (
        <SalvTable
          title='Transactions'
          columns={columns}
          data={data}
          selectedRows={transactionsPage.content.length ? selectedRows : undefined}
          setSelectedRows={setSelectedRows}
          isLoading={isFetching}
          emptyMessage='No Transactions attached to this case'
          hover
          expandable
          className='transaction-search-results'
          rowClick={(row) => navigate(getTransactionPath(row.original.id))}
          search
          onSearch={(value) => setQuery(value)}
          pagination={{
            type: SalvTablePaginationType.PAGINATION,
            total: transactionsPage?.total ?? 0,
            state: { pageIndex: page, pageSize: PAGE_SIZE },
            setPagination,
          }}
          sorting={{
            state: sortingState,
            setSorting,
          }}
          actions={
            <Has allPermissions={[CAN_MODIFY_CASE]}>
              <div style={{ display: 'flex', gap: '8px' }}>
                {selectedRows.length > 0 && (
                  <SalvButton
                    variant='muted'
                    size={Size.SMALL}
                    // TODO detach all when select all has been selected
                    onClick={handleDetach}
                    label='Detach Selected'
                    disabled={detachCaseTransactions.isPending}
                  />
                )}
                <SalvButton
                  variant='secondary'
                  size={Size.SMALL}
                  onClick={() =>
                    navigate({
                      pathname: InternalNavigation.transactionSearch,
                      search: createSearchParams({
                        [SearchParamKeys.caseHandle]: handle,
                      }).toString(),
                    })
                  }
                  label='Attach Transactions'
                />
              </div>
            </Has>
          }
        />
      )}
    </Element>
  );
};

export default CaseTransactions;
